import { useForm, useFormContext, useWatch } from 'react-hook-form';

import { PartyIdentifierDTO } from 'support/types';

import { EphemeralPartnerAnnotation } from './findEphemeralPartnerAnnotation';

export type EphemeralPartnerDisplayData = {
  partnerName: string;
  partnerIdentifiers: Array<PartyIdentifierDTO>;
};

type WatchedParty = {
  type?: string;
  name?: string;
  identifiers?: Record<string, unknown>;
};

const FORM_KEY_TO_CANONICAL_DOMAIN: Record<string, PartyIdentifierDTO['domain']> = {
  gln: 'GS1',
  vat: 'vatID',
  sender_internal: 'internal',
  receiver_internal: 'internal',
  duns: 'DUNS',
  federal_tax: 'federalTaxID',
  vvvo: 'VVVO',
  veterinary: 'veterinary',
  organic_control_point_number: 'organicControlPointNumber',
};

export const useEphemeralPartnerDisplayData = (
  ephemeralAnnotation: EphemeralPartnerAnnotation,
): EphemeralPartnerDisplayData => {
  const formContext = useFormContext<Record<string, unknown>>();
  const fallbackForm = useForm<Record<string, unknown>>({ defaultValues: {} });
  const activeControl = formContext?.control ?? fallbackForm.control;
  const watchedParties = useWatch({ control: activeControl, name: 'parties' }) as Array<WatchedParty> | undefined;

  let partnerName = ephemeralAnnotation.name;
  let partnerIdentifiers = ephemeralAnnotation.identifiers;

  if (Array.isArray(watchedParties)) {
    const matchedParty = watchedParties.find((party) => party?.type === ephemeralAnnotation.partyKey);
    if (matchedParty) {
      if (typeof matchedParty.name === 'string') {
        partnerName = matchedParty.name;
      }
      if (matchedParty.identifiers && typeof matchedParty.identifiers === 'object') {
        partnerIdentifiers = buildIdentifiersFromForm(matchedParty.identifiers);
      }
    }
  }

  return { partnerName, partnerIdentifiers };
};

const buildIdentifiersFromForm = (formIdentifiers: Record<string, unknown>): Array<PartyIdentifierDTO> => {
  const collected: Array<PartyIdentifierDTO> = [];
  const seenDomains = new Set<PartyIdentifierDTO['domain']>();

  for (const [formKey, rawValue] of Object.entries(formIdentifiers)) {
    if (typeof rawValue !== 'string') {
      continue;
    }
    const trimmedValue = rawValue.trim();
    if (trimmedValue.length === 0) {
      continue;
    }
    const canonicalDomain = FORM_KEY_TO_CANONICAL_DOMAIN[formKey];
    if (!canonicalDomain) {
      continue;
    }
    if (seenDomains.has(canonicalDomain)) {
      continue;
    }
    seenDomains.add(canonicalDomain);
    collected.push({ domain: canonicalDomain, identifier: trimmedValue });
  }

  return collected;
};
